import { Color3, type Mesh, MeshBuilder, Quaternion, StandardMaterial, Vector3 } from "@babylonjs/core";
import { Btn, HeroId, Slot, fxk, hv, pk, zk } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import { bell, click, fm, noise, thump, trim, whoosh } from "../../audio/synth.ts";
import type { Cue } from "../../audio/engine.ts";
import type { ClientHero, FxCtx, ProjectileFrame, ZoneFrame } from "../types.ts";

const V = { flock: 0, ultShot: 1 } as const;
const PK = { Piercer: pk(HeroId.Hella, 0), Soul: pk(HeroId.Hella, 1), DeathCrow: pk(HeroId.Hella, 2) } as const;
const ZK = { Thorn: zk(HeroId.Hella, 0), Crowstorm: zk(HeroId.Hella, 1) } as const;
const FX = { Thorn: fxk(HeroId.Hella, 0), Hel: fxk(HeroId.Hella, 1), Crow: fxk(HeroId.Hella, 2) } as const;
const hz = (s: number) => 293.66 * 2 ** (s / 12);
const cue = (db: number, body: Cue) => trim(db, body);

const thornFire = cue(15, (o) => {
  fm(o, { f: 1800, ratio: 2.1, index: 3, index1: 0, gain: 0.1, d: 0.12 });
  whoosh(o, 0.18, 1200, 4200, 0.08);
  return 0.22;
});
const deathCast = cue(3, (o) => {
  thump(o, 0.5, 80, 32, 0.35);
  noise(o, { color: "pink", type: "bandpass", f: 500, f1: 1700, gain: 0.4, d: 0.5 });
  bell(o, hz(18), 0.35, 0.45);
  return 0.8;
});
const thornReload =
  (seconds: number): Cue =>
  cue(9, (o) => {
    click(o, 0.16, 2600, 0.006, seconds * 0.18);
    fm(o, { f: 900, ratio: 1.9, index: 2.2, index1: 0, gain: 0.08, d: 0.18, delay: seconds * 0.32 });
    whoosh(o, 0.16, 700, 2300, 0.22, seconds * 0.55);
    click(o, 0.18, 3800, 0.004, seconds * 0.82);
    return seconds + 0.12;
  });
const theme = {
  bpm: 88,
  octave: -1,
  voice: (o, f, d, v) => fm(o, { f, ratio: 1.5, index: 2.8, index1: 0.4, gain: v * 0.16, d }),
  double: (o, f, d, v) => bell(o, f * 2, d, v * 0.4),
  notes: [
    [0, 1],
    [4, 0.5],
    [6, 0.5],
    [12, 2],
  ],
} satisfies ClientHero["audio"]["theme"];

function orient(m: { position: Vector3; scaling: Vector3; rotationQuaternion: Quaternion | null }, from: Vector3, to: Vector3) {
  const len = Vector3.Distance(from, to);
  m.position = Vector3.Lerp(from, to, 0.5);
  m.scaling.y = len;
  const dir = to.subtract(from).normalize();
  const axis = Vector3.Cross(Vector3.Up(), dir);
  const angle = Math.acos(Math.max(-1, Math.min(1, Vector3.Dot(Vector3.Up(), dir))));
  m.rotationQuaternion = axis.lengthSquared() > 1e-8 ? Quaternion.RotationAxis(axis.normalize(), angle) : null;
}

type HellaGear = { blade: Mesh; aura: Mesh; cape: Mesh; wings: Mesh[]; crown: Mesh; hornTips: Mesh[] };

const hero: ClientHero<HellaGear> = {
  id: HeroId.Hella,
  bulk: 1.05,
  shape: { head: 0.95, torso: 1.05, arms: 0.95, legs: 1.08 },
  gear(g) {
    const { R, H, chestH, scene, add, addWorld, rig, glow, toon } = g;
    const crown = add(MeshBuilder.CreateCylinder("antler-crown", { diameterTop: R * 1.7, diameterBottom: R * 0.9, height: 0.18, tessellation: 8 }, scene), rig.head, glow("#91ffbd"));
    crown.position.y = R * 0.75;
    const hornTips: Mesh[] = [];
    const wings: Mesh[] = [];
    for (const side of [-1, 1]) {
      for (let i = 0; i < 3; i++) {
        const horn = add(MeshBuilder.CreateBox("crown-spire", { width: 0.055, height: H * (0.32 - i * 0.035), depth: 0.055 }, scene), rig.head, glow(i === 0 ? "#91ffbd" : "#0b4e2b"));
        horn.position.set(side * R * (0.46 + i * 0.23), R * (0.88 + i * 0.1), i * 0.015);
        horn.rotation.z = -side * (0.38 + i * 0.18);
        hornTips.push(horn);
      }
      const wing = add(MeshBuilder.CreateBox("crow-wing", { width: R * 0.9, height: 0.08, depth: H * 0.36 }, scene), rig.torso, toon("#050805"));
      wing.position.set(side * R * 0.7, chestH * 0.35, R * 0.7);
      wing.rotation.z = side * 0.25;
      wings.push(wing);
    }
    const cape = add(MeshBuilder.CreateBox("grave-cape", { width: R * 1.45, height: H * 0.72, depth: 0.045 }, scene), rig.torso, toon("#010402"));
    cape.position.set(0, -H * 0.18, R * 0.78);
    cape.rotation.x = 0.08;
    const blade = add(MeshBuilder.CreateBox("night-thorn", { width: 0.1, height: 0.1, depth: 0.9 }, scene), rig.handR, glow("#91ffbd"));
    blade.position.z = -0.38;
    const aura = addWorld(MeshBuilder.CreateTorus("crownfall-ring", { diameter: 2, thickness: 0.04, tessellation: 32 }, scene), glow("#91ffbd", 0.35));
    aura.setEnabled(false);
    return { blade, aura, cape, wings, crown, hornTips };
  },
  animate(gear, f, rig) {
    gear.blade.scaling.setAll(1 + (f.action === 1 ? 0.25 : 0));
    const flying = f.ultActive || f.v[V.flock] > 0.5;
    gear.cape.rotation.x = 0.08 + Math.sin(f.t * 5) * 0.03 + (f.ultActive ? 0.22 : 0);
    for (const [i, wing] of gear.wings.entries()) wing.rotation.z = (i === 0 ? -1 : 1) * (0.22 + (flying ? 0.38 : 0) + Math.sin(f.t * 7 + i) * 0.04);
    for (const [i, tip] of gear.hornTips.entries()) tip.visibility = 0.78 + Math.sin(f.t * 4 + i) * 0.18;
    gear.aura.setEnabled(f.ultActive);
    if (f.ultActive) {
      gear.aura.position.set(f.pos.x, f.pos.y + 0.08, f.pos.z);
      gear.aura.rotation.y += f.dt * 2.5;
      gear.aura.scaling.setAll(2 + Math.sin(f.t * 5) * 0.15);
    }
    for (const p of Object.values(rig.parts)) p.visibility = f.v[V.flock] > 0.5 ? 0.38 : 1;
    return flying ? "holding-both" : undefined;
  },
  hud(c, eid) {
    return [{ label: "Crow form", value: hv(c, eid, V.flock) }];
  },
  lifting(c, eid, buttons, grounded) {
    return !grounded && !!(buttons & Btn.Jump) && !hv(c, eid, V.flock);
  },
  projectiles: {
    [PK.Piercer]: (scene, _f: ProjectileFrame) => {
      const m = MeshBuilder.CreateCylinder("thorn", { diameterTop: 0, diameterBottom: 0.14, height: 0.7, tessellation: 6 }, scene);
      m.material = new StandardMaterial("thorn-mat", scene);
      (m.material as StandardMaterial).emissiveColor = Color3.FromHexString("#91ffbd");
      return {
        gear: m,
        update(_mesh, fr) {
          m.position.set(fr.pos.x, fr.pos.y, fr.pos.z);
          m.lookAt(m.position.add(new Vector3(fr.dir.x, fr.dir.y, fr.dir.z)));
          m.rotation.x += Math.PI / 2;
        },
        dispose: () => m.dispose(),
      };
    },
    [PK.DeathCrow]: (scene, _f) => {
      const body = MeshBuilder.CreateSphere("crow", { diameter: 0.42, segments: 8 }, scene);
      body.material = new StandardMaterial("crow-mat", scene);
      (body.material as StandardMaterial).emissiveColor = Color3.FromHexString("#b5ffd0");
      return {
        gear: body,
        update(_mesh, fr) {
          body.position.set(fr.pos.x, fr.pos.y, fr.pos.z);
          body.rotation.y += fr.dt * 12;
          body.scaling.x = 1.4;
        },
        dispose: () => body.dispose(),
      };
    },
    [PK.Soul]: (scene, _f) => {
      const core = MeshBuilder.CreateSphere("soul-orb", { diameter: 0.46, segments: 12 }, scene);
      core.material = new StandardMaterial("soul-orb-mat", scene);
      (core.material as StandardMaterial).emissiveColor = Color3.FromHexString("#45ff98");
      const ring = MeshBuilder.CreateTorus("soul-orb-ring", { diameter: 0.62, thickness: 0.035, tessellation: 18 }, scene);
      ring.material = core.material;
      ring.parent = core;
      return {
        gear: core,
        update(_mesh, fr) {
          core.position.set(fr.pos.x, fr.pos.y, fr.pos.z);
          core.rotation.y += fr.dt * 8;
          ring.rotation.x += fr.dt * 11;
        },
        dispose: () => core.dispose(),
      };
    },
  },
  zones: {
    [ZK.Thorn]: (scene, f: ZoneFrame) => {
      const m = MeshBuilder.CreateTorus("thorn-timer", { diameter: f.radius * 2, thickness: 0.035, tessellation: 24 }, scene);
      m.material = new StandardMaterial("thorn-zone", scene);
      (m.material as StandardMaterial).emissiveColor = Color3.FromHexString("#91ffbd");
      (m.material as StandardMaterial).alpha = 0.42;
      return {
        gear: m,
        update(_mesh, fr) {
          m.position.set(fr.pos.x, fr.pos.y + 0.05, fr.pos.z);
          m.scaling.setAll(Math.max(0.1, fr.ticks / 180));
        },
        dispose: () => m.dispose(),
      };
    },
    [ZK.Crowstorm]: (scene, f: ZoneFrame) => {
      const m = MeshBuilder.CreateTorus("crowstorm-timer", { diameter: f.radius * 2, thickness: 0.08, tessellation: 36 }, scene);
      m.material = new StandardMaterial("crowstorm-zone", scene);
      (m.material as StandardMaterial).emissiveColor = Color3.FromHexString("#d8ffe7");
      (m.material as StandardMaterial).alpha = 0.38;
      return {
        gear: m,
        update(_mesh, fr) {
          m.position.set(fr.pos.x, fr.pos.y + 0.08, fr.pos.z);
          m.rotation.y += fr.dt * 4;
          m.scaling.setAll(0.35 + (1 - fr.ticks / 108) * 0.8);
        },
        dispose: () => m.dispose(),
      };
    },
  },
  icons: {
    [Slot.Primary]: "M5 12 L19 5 M8 16 L19 5 M12 19 L19 5",
    [Slot.Secondary]: "M6 6 L18 18 M18 6 L6 18 M12 4 L12 20",
    [Slot.Ability1]: "M4 15 C7 8 10 6 12 4 C14 6 17 8 20 15 M8 14 C10 12 14 12 16 14",
    [Slot.Ability2]: "M12 4 C17 8 18 13 12 20 C6 13 7 8 12 4 M8 12 H16",
    [Slot.Ultimate]: "M12 3 L15 10 L22 12 L15 14 L12 21 L9 14 L2 12 L9 10 Z",
    passive: "M6 18 C10 14 14 14 18 18 M7 12 C10 9 14 9 17 12",
  },
  fx: {
    [FX.Thorn]: {
      tracer(c, from, to) {
        const m = MeshBuilder.CreateCylinder("green-tracer", { diameter: 0.045, height: 1, tessellation: 6 }, c.scene);
        m.material = c.glow("#91ffbd");
        orient(m, new Vector3(from.x, from.y, from.z), new Vector3(to.x, to.y, to.z));
        c.timed(m, 0.12, (t, mesh) => (mesh.visibility = 1 - t));
      },
      explosion(c, pos, radius) {
        ring(c, pos, radius, "#91ffbd");
      },
    },
    [FX.Crow]: { explosion: (c, pos, radius) => ring(c, pos, radius, "#d8ffe7") },
    [FX.Hel]: { explosion: (c, pos, radius) => ring(c, pos, radius, "#45ff98") },
  },
  audio: {
    weight: "light",
    fire: (ctx) => ({ key: ctx?.ult ? "hella-crow" : "hella-thorn", cue: thornFire }),
    reload: thornReload,
    ability: {
      [Slot.Secondary]: deathCast,
      [Slot.Ability1]: deathCast,
      [Slot.Ability2]: deathCast,
      [Slot.Ultimate]: deathCast,
    },
    impacts: { [FX.Thorn]: thornFire, [FX.Hel]: deathCast, [FX.Crow]: deathCast },
    explosions: { [FX.Thorn]: () => deathCast, [FX.Hel]: () => deathCast, [FX.Crow]: () => deathCast },
    flight: { [PK.DeathCrow]: loops.flight("missile") },
    loops: ({ v, alive }) => (alive && v[V.flock] > 0.5 ? [{ key: "flock", build: loops.levitate, gain: 0.9 }] : []),
    selfLift: ({ alive, ultActive }) => (alive && ultActive ? loops.barrage : undefined),
    theme,
    themeTrim: { heroic: 1.5, hostile: -6.5, tease: 7.5 },
  },
};

function ring(c: FxCtx, pos: { x: number; y: number; z: number }, radius: number, color: string) {
  const m = MeshBuilder.CreateTorus("death-ring", { diameter: 2, thickness: 0.14, tessellation: 36 }, c.scene);
  m.material = c.glow(color, 0.55);
  m.position.set(pos.x, pos.y + 0.08, pos.z);
  c.timed(m, 0.36, (t, mesh) => {
    mesh.scaling.setAll(radius * (0.2 + t));
    mesh.visibility = 1 - t;
  });
}

export default hero;
